<template>
  <v-card>
    <v-card-title>Agenda - Disponibilidade</v-card-title>

    <v-card-text>
      <div v-if="isGestor">
        <v-row class="mb-4">
          <v-col
            cols="12"
            sm="6"
          >
            <v-autocomplete
              v-model="agenteSelecionadoId"
              :items="listaAgentes"
              item-text="nome"
              item-value="id"
              label="Selecione o Agente"
              placeholder="Pesquise por nome"
              outlined
              dense
              hide-details
              @change="handleAgenteChange"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-expansion-panels
          focusable
          :value="expansionPanelQueDeveAbrirPorDefault"
        >
          <agenda-disponibilidade-expansion-panel
            v-if="agenteSelecionadoId"
            :agente-id="agenteSelecionadoId"
            :disponibilidades-do-agente="disponibilidadesDoAgente"
            :pode-editar="true"
            @refetch-data="getDisponibilidades"
          />

          <agenda-indisponibilidade-expansion-panel
            v-if="agenteSelecionadoId"
            :agente-id="agenteSelecionadoId"
            :indisponibilidades-do-agente="indisponibilidadesDoAgente"
            :is-get-indisponibilidades-loading="isGetIndisponibilidadesLoading"
            :pode-editar="true"
            @refetch-data="getIndisponibilidades"
          />

          <v-alert
            v-else
            type="info"
            text
            class="mt-2"
          >
            Selecione um agente acima para gerenciar a disponibilidade.
          </v-alert>
        </v-expansion-panels>
      </div>

      <div v-else>
        <v-alert
          type="warning"
          outlined
          class="mt-2"
        >
          Você não tem permissão para visualizar ou editar configurações de disponibilidade.
        </v-alert>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { onMounted, ref } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import AgendaDisponibilidadeExpansionPanel from '@/views/agenda/disponibilidade/AgendaDisponibilidadeExpansionPanel.vue'
import AgendaIndisponibilidadeExpansionPanel from '@/views/agenda/disponibilidade/AgendaIndisponibilidadeExpansionPanel.vue'
import AgenteService from '@/services/modules/AgenteService'
import ColaboradorService from '@/services/modules/ColaboradorService'
import useUserStore from '@/store/user'
import useAuthStore from '@/store/auth'
import { getTimeStringComApenasHorasEMinutos } from '@/utils/dateTime'

export default {
  components: {
    AgendaDisponibilidadeExpansionPanel,
    AgendaIndisponibilidadeExpansionPanel,
  },

  setup() {
    // 1. Stores
    const userStore = useUserStore()
    const authStore = useAuthStore()

    // CORRECTION HERE: Extract 'id' directly and rename it to avoid confusion
    // The store does not have a 'user' object, it has 'id', 'nome', etc.
    const { id: usuarioLogadoId } = storeToRefs(userStore)
    const { isGestor } = storeToRefs(authStore)

    // 2. State
    const disponibilidadesDoAgente = ref([])
    const indisponibilidadesDoAgente = ref([])
    const isGetDisponibilidadesLoading = ref(false)
    const isGetIndisponibilidadesLoading = ref(false)
    const expansionPanelQueDeveAbrirPorDefault = ref(0)

    const listaAgentes = ref([])
    const agenteSelecionadoId = ref(null)

    // 3. Fetch Functions
    async function getDisponibilidades() {
      if (!agenteSelecionadoId.value) return
      try {
        isGetDisponibilidadesLoading.value = true
        const data = await AgenteService.getDisponibilidades(agenteSelecionadoId.value)
        disponibilidadesDoAgente.value = data.map(it => ({
          ...it,
          horaInicial: getTimeStringComApenasHorasEMinutos(it.horaInicial),
          horaFinal: getTimeStringComApenasHorasEMinutos(it.horaFinal),
        }))
      } finally {
        isGetDisponibilidadesLoading.value = false
      }
    }

    async function getIndisponibilidades() {
      if (!agenteSelecionadoId.value) return
      try {
        isGetIndisponibilidadesLoading.value = true
        const data = await AgenteService.getIndisponibilidades(agenteSelecionadoId.value)
        indisponibilidadesDoAgente.value = data
      } finally {
        isGetIndisponibilidadesLoading.value = false
      }
    }

    function refreshAllData() {
      getDisponibilidades()
      getIndisponibilidades()
    }

    async function fetchListaAgentes() {
      try {
        const response = await ColaboradorService.get()
        listaAgentes.value = response.data || response
      } catch (error) {
        console.error(error)
      }
    }

    function handleAgenteChange() {
      refreshAllData()
    }

    // 4. Lifecycle
    onMounted(async () => {
      // If not Gestor, do nothing (block access)
      if (!isGestor.value) {
        return
      }

      // If Gestor, fetch list
      await fetchListaAgentes()

      // CORRECTION HERE: Use the correct variable for the logged-in user ID
      if (usuarioLogadoId.value) {
        agenteSelecionadoId.value = usuarioLogadoId.value
        refreshAllData()
      }
    })

    return {
      expansionPanelQueDeveAbrirPorDefault,
      disponibilidadesDoAgente,
      indisponibilidadesDoAgente,
      isGetIndisponibilidadesLoading,
      isGetDisponibilidadesLoading,
      getDisponibilidades,
      getIndisponibilidades,
      isGestor,
      listaAgentes,
      agenteSelecionadoId,
      handleAgenteChange,
    }
  },
}
</script>
